/**
 * Utility functions for rendering fractions in text
 */

/**
 * Renders fractions in text as HTML with proper styling
 * @param {string} text - Text containing fractions
 * @returns {string} HTML string with formatted fractions
 */
export const renderFractions = (text) => {
  if (!text) return text;
  
  let result = text;
  
  // Replace mixed fractions first (e.g., "2 1/3")
  result = result.replace(/(\d+)\s+(\d+)\s*\/\s*(\d+)/g, 
    '<span class="mixed-fraction">' +
    '<span class="whole">$1</span>' +
    '<span class="fraction">' +
    '<span class="numerator">$2</span>' +
    '<span class="denominator">$3</span>' +
    '</span>' +
    '</span>'
  );
  
  // Replace simple fractions (e.g., "3/4")
  result = result.replace(/(\d+)\s*\/\s*(\d+)/g, 
    '<span class="fraction">' +
    '<span class="numerator">$1</span>' +
    '<span class="denominator">$2</span>' +
    '</span>'
  );
  
  return result;
};

/**
 * Checks if text contains fractions
 * @param {string} text - Text to check
 * @returns {boolean} True if text contains fractions
 */
export const containsFractions = (text) => {
  if (!text) return false;
  return /\d+\s*\/\s*\d+/.test(text);
};

/**
 * Validates fraction syntax
 * @param {string} text - Text to validate
 * @returns {object} Validation result with isValid and errors
 */
export const validateFractions = (text) => {
  if (!text) return { isValid: true, errors: [] };
  
  const errors = [];
  
  // Check for invalid fraction patterns
  const invalidPatterns = [
    /\/\s*0/g, // Division by zero
    /\d+\s*\/\s*$/g, // Incomplete fractions
    /^\s*\/\s*\d+/g, // Missing numerator
  ];
  
  invalidPatterns.forEach((pattern, index) => {
    if (pattern.test(text)) {
      switch (index) {
        case 0:
          errors.push('Division by zero is not allowed');
          break;
        case 1:
          errors.push('Incomplete fraction (missing denominator)');
          break;
        case 2:
          errors.push('Incomplete fraction (missing numerator)');
          break;
      }
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Gets example fraction formats for help text
 * @returns {Array} Array of example formats
 */
export const getFractionExamples = () => {
  return [
    '3/4 (simple fraction)',
    '2 1/3 (mixed fraction)', 
    'The answer is 5/8 (text with fraction)',
    '1/2 + 3/4 = 5/4 (multiple fractions)'
  ];
};
